import Swal from "sweetalert2";

import withReactContent from "sweetalert2-react-content";

// SweetAlert2 maneja un solo popup a la vez, y el velo (el backdrop) lo saca
// recién cuando termina la animación de cierre. Si se dispara un aviso mientras
// otro se está cerrando, las dos animaciones se pisan y a veces queda el velo en
// pantalla sin ningún diálogo adentro, con la página trabada.
// Por eso todos los avisos salen por acá: se encolan, y cada uno espera a que el
// anterior se haya cerrado del todo.
//
// Los avisos modales van además con `animation: false` (ver SIN_ANIMACION). Para
// animar la entrada, SweetAlert2 le pone al popup `opacity: 0 !important` y agenda
// un setTimeout de 10 ms que recién ahí lo hace visible. Si ese timer no llega a
// correr —pasa cuando el aviso se dispara en el mismo gesto que cambia de pantalla,
// como al importar partidos— queda el velo tapando todo y ningún diálogo visible:
// la página no responde y no hay forma de cerrarlo. Sin animación no hay truco de
// opacity ni timer, así que el diálogo siempre se ve.
let cola = Promise.resolve();

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function fire(instance, params) {
  let avisarCierre;
  const cerrado = new Promise((resolve) => (avisarCierre = resolve));
  const resultado = cola.then(() =>
    instance.fire({
      ...params,
      didClose: () => {
        if (params.didClose) params.didClose();
        avisarCierre();
      },
    })
  );
  // `resultado` se resuelve cuando el usuario cierra el aviso (o vence el timer),
  // pero el velo sigue un rato más, hasta el didClose: el próximo espera eso.
  // El tope es por si el didClose no llega (popup destruido por afuera), para que
  // la cola no quede trabada para siempre.
  cola = resultado.then(() => Promise.race([cerrado, esperar(1000)])).catch(() => {});
  return resultado;
}

// Los avisos que tapan la pantalla con el velo se muestran sin animación de entrada.
// Los toasts de esquina no llevan velo: si fallaran no trabarían nada, así que
// conservan la suya.
const SIN_ANIMACION = { animation: false };

export function toastError(title, timeout = 1500) {
  var Toast = Swal.mixin({
    toast: true,
    position: "top-right",
    iconColor: "white",
    background: "#f27474",
    timerProgressBar: true,
    showConfirmButton: false,
    timer: timeout,
  });
  const MySwal = withReactContent(Toast);
  fire(MySwal, {
    icon: "error",
    title: "<span style='color: #fff'>" + title + "</span>",
  });
}

export function toastBigSuccess(title, text) {
  var Alert = Swal.mixin({
    ...SIN_ANIMACION,
    confirmButtonText: `Aceptar`,
    confirmButtonColor: "#84bca3", //"#888888",
    icon: "success",
  });
  const MySwal = withReactContent(Alert);
  fire(MySwal, {
    title: title,
    text: text,
  });
}
export function toastBigInfo(title, text) {
  var Alert = Swal.mixin({
    ...SIN_ANIMACION,
    confirmButtonText: `Aceptar`,
    confirmButtonColor: "#84bca3", //"#888888",
    icon: "info",
  });
  const MySwal = withReactContent(Alert);
  fire(MySwal, {
    title: title,
    text: text,
  });
}

export function toastInfo(title, timeout = 2000) {
  var Toast = Swal.mixin({
    toast: true,
    position: "bottom",
    iconColor: "white",
    background: "#87adbd",
    timerProgressBar: true,
    showConfirmButton: false,
    timer: timeout,
  });
  const MySwal = withReactContent(Toast);
  fire(MySwal, {
    icon: "info",
    title: "<span style='color: #fff'>" + title + "</span>",
  });
}

export function toastConfirm(title, text = "", callbackTrue = null, callbackFalse = null) {
  var Alert = Swal.mixin({
    ...SIN_ANIMACION,
    showDenyButton: true,
    showCancelButton: false,
    confirmButtonText: `Si`,
    confirmButtonColor: "#84bca3", //"#888888",
    denyButtonColor: "#e77e89", //"#AAAAAA",
    denyButtonText: `No`,
    text: text,
    customClass: {
      confirmButton: "order-1",
      denyButton: "order-2",
    },
  });
  const MySwal = withReactContent(Alert);
  fire(MySwal, {
    title: title,
  }).then((result) => {
    if (result.isConfirmed) {
      if (callbackTrue) callbackTrue();
    } else {
      if (callbackFalse) callbackFalse();
    }
  });
}

export function toastTriState(title, label1, label2, label3, callbackConfirmed = null, callbackDenied = null, callbackCanceled = null) {
  var Alert = Swal.mixin({
    ...SIN_ANIMACION,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonColor: "#e77e89",
    confirmButtonText: label1,
    denyButtonColor: "#fed678",
    denyButtonText: label2,
    cancelButtonColor: "#84bca3",
    cancelButtonText: label3,
    customClass: {
      confirmButton: "order-1",
      denyButton: "order-2",
      cancelButton: "order-3",
    },
  });
  const MySwal = withReactContent(Alert);
  fire(MySwal, {
    title: title,
  }).then((result) => {
    if (result.isConfirmed) {
      if (callbackConfirmed) callbackConfirmed();
    } else if (result.isDenied) {
      if (callbackDenied) callbackDenied();
    } else {
      if (callbackCanceled) callbackCanceled();
    }
  });
}

export function toastBigError(title, text) {
  var Alert = Swal.mixin({
    ...SIN_ANIMACION,
    confirmButtonText: `Aceptar`,
    confirmButtonColor: "#84bca3", //"#888888",
    icon: "error",
  });
  const MySwal = withReactContent(Alert);
  fire(MySwal, {
    title: title,
    text: text,
  });
}

export function toastTimeout(title, html, timer, timerProgressBar, icon, callBack) {
  let timerInterval;
  fire(Swal, {
    ...SIN_ANIMACION,
    title: title,
    html: html,
    timer: timer,
    timerProgressBar: timerProgressBar,
    icon: icon,
    didOpen: () => {
      Swal.showLoading();
      // timerInterval = setInterval(() => {
      //   const content = Swal.getContent()
      //   if (content) {
      //     const b = content.querySelector('b')
      //     if (b) {
      //       b.textContent = Swal.getTimerLeft()
      //     }
      //   }
      // }, 100)
    },
    willClose: () => {
      clearInterval(timerInterval);
    },
  }).then((result) => {
    /* Read more about handling dismissals below */
    if (result.dismiss === Swal.DismissReason.timer) {
      // console.log('I was closed by the timer')
      callBack();
    }
  });
}

// .colored-toast.swal2-icon-success {
//   background-color: #a5dc86 !important;
// }

// .colored-toast.swal2-icon-error {
//   background-color: #f27474 !important;
// }

// .colored-toast.swal2-icon-warning {
//   background-color: #f8bb86 !important;
// }

// .colored-toast.swal2-icon-info {
//   background-color: #3fc3ee !important;
// }

// .colored-toast.swal2-icon-question {
//   background-color: #87adbd !important;
// }

// .colored-toast .swal2-title {
//   color: white;
// }

// .colored-toast .swal2-close {
//   color: white;
// }

// .colored-toast .swal2-content {
//   color: white;
// }
